import { useEffect, useRef } from "react";
import { useAuthStore } from "@/stores/auth-store";

interface AuthenticatedHtmlContentProps {
  html: string;
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
}

/**
 * Renders sanitized HTML content and authenticates any <img> tags that use
 * data-auth-src (set by sanitizeFogBugzHtml). Fetches each image with Bearer
 * auth and replaces data-auth-src with a blob URL in the real src attribute.
 */
export function AuthenticatedHtmlContent({
  html,
  className,
  onClick,
  onKeyDown,
}: AuthenticatedHtmlContentProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;

    const images = ref.current.querySelectorAll<HTMLImageElement>(
      "img[data-auth-src]"
    );
    if (images.length === 0) return;

    const token = useAuthStore.getState().accessToken;
    const controllers: AbortController[] = [];
    const blobUrls: string[] = [];
    let cancelled = false;

    images.forEach((img) => {
      const authSrc = img.getAttribute("data-auth-src");
      if (!authSrc) return;

      const controller = new AbortController();
      controllers.push(controller);

      const headers: Record<string, string> = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      fetch(authSrc, { headers, signal: controller.signal })
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.blob();
        })
        .then((blob) => {
          if (cancelled) {
            URL.revokeObjectURL(URL.createObjectURL(blob));
            return;
          }
          const blobUrl = URL.createObjectURL(blob);
          blobUrls.push(blobUrl);
          img.src = blobUrl;
          img.classList.remove("animate-pulse", "bg-muted");
        })
        .catch((err) => {
          if (err.name !== "AbortError" && !cancelled) {
            img.remove();
          }
        });
    });

    return () => {
      cancelled = true;
      controllers.forEach((c) => c.abort());
      blobUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [html]);

  return (
    <div
      ref={ref}
      className={className}
      dangerouslySetInnerHTML={{ __html: html }}
      onClick={onClick}
      onKeyDown={onKeyDown}
    />
  );
}
